import React, { useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { i18nIntl, formatNumber, imgPath, isNotEmpty, getRemotedbUrl, adaptGameToContainer, isDesktop } from '../../../lib/ep.js';
import { useCSS, useImageFallback } from '../../../lib/hooks.js';
import { stage_promotion_types as spt } from '../../../data/bootstrap_stages.js'

import { SquaredButton } from '../../builder/ActionButton';
import ButtonCollection from '../../builder/ButtonCollection';
import FieldLabel from '../../builder/FieldLabel';
import FloatingLayer from '../../builder/FloatingLayer';
import SectionTitle from '../../builder/SectionTitle';
import Tooltip from '../../builder/Tooltip';

import FloatingLayerPublic from '../../front/FloatingLayerPublic';
import SingleGallery from '../../front/SingleGallery';

import QuizStatsPublic from '../../front/quiz/QuizStatsPublic';
import MultiEntriesRanking from '../../users/MultiEntriesRanking';
import LeveledHTMLText from '../helpers/LeveledHTMLText.js';
import { UserTeamParticipationDetail } from '../../../containers/TeamClient.js';
import ParticipationStatus from '../helpers/ParticipationStatus.js';
import { DisplayDynamicForm } from '../../builder/DynamicForm.js';
import { localizeFields } from '../../builder/DynamicFields.js';
import ActionLink from '../../builder/ActionLink.js';
import PickFavoriteFinal from '../PickFavoriteFinal.js';
import BattleFinal from '../BattleFinal.js';
import LikeDislikeFinal from '../LikeDislikeFinal.js';
import { GuessScoreResumeWithPoints } from '../GuessScoreResume.js';
import OneTimeBracketFinal from '../OneTimeBracketFinal.js';
import RibbonCollection from '../../ui_items/RibbonCollection.js';

const max_paricipations_to_show = 50;

const formatStageName = (stage, intl) => {
	if(isNotEmpty(stage, 'display_title')) return stage.display_title;
	
	const i18n = (m, c) => i18nIntl(m, intl, c);

	return `${i18n("Stage")}${(stage.order != undefined) ? ` #${stage.order}` : ""}`;
};

export const TicketUserData = ({ data, fields, promotion_id, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const graphic_info = data?.graphic_info ?? false;
	const force_graphic_info = props?.force_graphic_info ?? false;
	const display_ticket = props?.display_ticket ?? true;

	if(graphic_info === false && force_graphic_info) return null;
	let localized_fields = fields;
	if (data.ticket_extra_data && fields) {
		localized_fields = localizeFields(fields,props.locale);
	}

	let image = null;
	if(display_ticket && graphic_info !== false) {
		const img_basedir = imgPath(EP.BUCKET, promotion_id, 'entries/customfields/large');

		image = useImageFallback([
			`${img_basedir}original_${data.graphic_info}`,
			`${img_basedir}${data.graphic_info}`,
		]);
	}

	return (
		<>
			{(isNotEmpty(data, 'code')) && (
				<div class="gui_not_in_bottom_small">
					<span>{i18n("Code")}: <strong>{data.code}</strong></span>
				</div>
			)}

			{(isNotEmpty(data, 'ticket_amount')) && (
				<div class="gui_not_in_bottom_small">
					<span>{i18n("Amount")}: <strong>{data.ticket_amount}</strong></span>
				</div>
			)}

			{(data?.ticket_extra_data && fields) && (
				localized_fields.map((item, k) => {
					let field_value = data.ticket_extra_data[item.id];
					if(!field_value) return null;

					let field_name = `${i18n("Extra field")} ${k + 1}`;
					field_name = (isNotEmpty(item, 'short_title')) ? item.short_title : field_name;
					field_name = (isNotEmpty(item, 'title')) ? item.title : field_name;

					const value = data.ticket_extra_data[item.id];

					let is_link = false;
					if(value.indexOf("http") == 0) is_link = true;	// regular URL starting with protocol
					if(value.indexOf("//") == 0) is_link = true;	// URL such as "//storage.googleapis.com/..."
					
					let is_image = false;
					if(is_link) {
						const lower_value = value.toLowerCase();
						if(lower_value.indexOf(".jpg") != -1) is_image = true;
						if(!is_image && lower_value.indexOf(".jpeg") != -1) is_image = true;
						if(!is_image && lower_value.indexOf(".png") != -1) is_image = true;
						if(!is_image && lower_value.indexOf(".gif") != -1) is_image = true;
						if(!is_image && lower_value.indexOf(".webp") != -1) is_image = true;
					}

					if(is_image) {
						return (
							<div key={`reqform${k}`} class="gui_not_in_bottom_small">
								<span style={{ display:'flex', alignItems:'center', gap:'0.5em' }}>
									{field_name}:

									<ActionLink _href={value} _target="_blank">
										<img src={value} alt="" style={{ height:'60px' }} />
									</ActionLink>
								</span>
							</div>
						)
					}

					if(is_link) {
						return (
							<div key={`reqform${k}`} class="gui_not_in_bottom_small">
								<span>{field_name}: <strong>
									<ActionLink
										_href={value}
										_target="_blank"
										autotranslate={false}
									>{i18n("View", 'lowercase')}</ActionLink>
								</strong></span>
							</div>
						)
					}

					return (
						<div key={`reqform${k}`} class="gui_not_in_bottom_small">
							<span>{field_name}: <strong>{value}</strong></span>
						</div>
					)
				})
			)}

			{(image) && (
				<a href={image} target='_blank'>
					<img src={image} style={{ maxWidth:'250px', maxHeight:'350px' }} alt="" />
				</a>
			)}
		</>
	)
};

export const TicketLayer = ({ participation, promotion, backButton, ...props }) => {
	return (
		<FloatingLayerPublic pm={promotion} title="Receipt" backButton={backButton}>
			<div>
				<TicketUserData
					data={participation?.StageTicket ?? (participation?.extra?.stage_ticket ?? [])}
					fields={participation.Stage?.reqs_form ?? (props?.stage?.reqs_form ?? [])}
					promotion_id={promotion.id}
					force_graphic_info={true}
					locale={props?.locale}
					intl={props.intl}
				/>
			</div>
		</FloatingLayerPublic>
	)
};

const SingleParticipation = ({ part, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const total_participations = props?.total_participations ?? 10;
	const counter = total_participations - (props?.i ?? 0);

	const stage = part.Stage;
	const stage_score = part.StageScore;

	let has_stage_custom_fields = false;
	if((part?.StageParticipation ?? []).length > 0) {
		part.StageParticipation.map((sp) => {
			if((sp?.config?.mediatype ?? 'text') == 'image') has_stage_custom_fields = true;
		});
	}

	const show_ticket = part?.StageTicket ?? false;
	const qz = stage_score?.quiz_entry ?? {};
	const display_details = props?.display_details ?? false;
	const display_prizes = props?.display_prizes ?? false;
	const display_date = props?.display_date ?? false;
	const display_origin = props?.display_origin ?? false;
	const display_complete_info = props?.display_complete_info ?? false;
	const show_media = (qz.media != undefined) ? true : false;
	const display_view = ['2', '3'].includes(stage_score.public_entry) ? false : true;	// false if "processing" (2) or "error" (3) 
	const in_scores = props?.in_scores ?? false;
	const has_tickets = props?.has_tickets ?? false;
	const has_ranking_enabled = props?.has_ranking_enabled ?? false;
	const has_score = props?.has_score ?? false;

	const tzcreated = moment.tz(stage_score[(!in_scores) ? 'created' : 'modified'], 'Europe/Madrid').tz(props.tz);

	return (
		<tr>
			<td class="ranking_pos mod_align_left gui_text_align_start">
				#{(total_participations > 10 && counter < 10) ? `0${counter}` : counter}
			</td>

			<td class="mod_align_left gui_text_align_start">
				<div class="sided_contents">
					{(display_complete_info && stage.icon != undefined) && (
						<Tooltip
							position="top"
							icon={stage.icon}
						>{stage.stage_type}</Tooltip>
					)}

					<div class="title is_multilined">
						<span class="gui_cutTextOnOverflow">
							{formatStageName(stage, props.intl)}

							{(stage_score.code) && (
								<em>{`${i18n("Code")}: ${stage_score.code}`}</em>
							)}
						</span>
					</div>
				</div>
			</td>

			{(display_date) && (
				<td class="mod_align_left gui_text_align_start">
					{tzcreated.format(((display_complete_info || !isDesktop()) ? 'L, LT' : 'LL, LT'))}
				</td>
			)}

			{(display_origin) && (
				<td class="mod_align_left gui_text_align_start">
					<div class="sided_contents">
						{(isNotEmpty(stage_score, 'ip')) && (
							<span style={{flexGrow:'1'}}>{stage_score.ip}</span>
						)}

						{(isNotEmpty(stage_score, 'device')) && (
							<Tooltip
								position="top"
								icon={`knk-${stage_score.device}`}
							>{stage_score.device_label}</Tooltip>
						)}

						{(isNotEmpty(stage_score, 'browser')) && (
							<Tooltip
								position="top"
								icon={`knk-${stage_score.browser}`}
							>{stage_score.browser_label}</Tooltip>
						)}
					</div>
				</td>
			)}

			{(has_ranking_enabled || has_score) && (
				<td class="gui_text_align_end">
					<span class="gui_flexbox_auto gui_flex_end gui_flex_align_center">
						<RibbonCollection
							items={[{
								title:formatNumber(stage_score.score),
								color:(isNotEmpty(part.Participation, 'completed')) ? ((part.Participation?.completed === true) ? 'green' : 'red') : ((true) ? '--text-color-rgb' : 'transparent'),
								tooltip:(isNotEmpty(part.Participation, 'completed')) ? <Tooltip
									with_icon={false}
								>{(part.Participation.completed === true) ? "Game completed" : "Game not completed"}</Tooltip> : null,
							}]}
						/>
					</span>
				</td>
			)}

			{(props?.display_actions ?? false) && (
				<td>
					<ButtonCollection _class={{other:'gui_flex_end'}}>
						{(has_stage_custom_fields) && (
							<SquaredButton
								icon='knk-information'
								onClick={() => props.displayAdditionalInformation(part)}
								tooltip={<Tooltip
									with_icon={false}
									position="top"
								>Additional fields</Tooltip>}
								aria-label="View additional information"
							/>
						)}

						{(display_prizes && (stage_score?.has_prize ?? false)) && (
							<SquaredButton
								icon='knk-winners'
								tooltip={<Tooltip
									with_icon={false}
									position="top"
								>Won a prize</Tooltip>}
								aria-label="The user won a prize"
							/>
						)}

						<ParticipationStatus
							status={stage_score.public_entry}
						/>

						{((display_details || show_media) && display_view) && (
							<SquaredButton
								onClick={() => props.displayDetails(part, show_media)}
								icon='knk-visible'
								_class={{ other:'original_color_item', variable_icon_color:true }}
								aria-label="View details"
							/>
						)}

						{((display_details || show_media) && !display_view) && (
							<SquaredButton
								disabled={true}
								icon='knk-visible'
								_class={{ other:'gui_withoutEvents' }}
							/>
						)}

						{(has_tickets && show_ticket) && (
							<SquaredButton
								icon='knk-buy-ticket'
								onClick={() => props.displayTicket(part)}
								_class={{ other:`original_color_item`, variable_icon_color:true }}
								aria-label="View ticket"
							/>
						)}

						{(has_tickets && !show_ticket) && (
							<SquaredButton
								icon='knk-white'
								_class={{ other:'gui_withoutEvents gui_withoutEvents disabledItem original_color_item', variable_icon_color:true }}
							/>
						)}
					</ButtonCollection>
				</td>
			)}
		</tr>
	)
}

const UserParticipationsMultistageList = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	useCSS('comp/quiz.css');

	const [ display_additional_participation, setDisplayAdditionalParticipation ] = useState(false);
	const [ display_participation_details, setDisplayParticipationDetails ] = useState(false);
	const [ display_voting_details, setDisplayVotingDetails ] = useState(false);
	const [ display_team_details, setDisplayTeamDetails ] = useState(false);
	const [ display_guess_score_details, setDisplayGuessScoreDetails ] = useState(false);
	const [ display_voting_stage_id, setDisplayVotingStageId ] = useState(false);
	const [ display_ticket_details, setDisplayTicketDetails ] = useState(false);
	const [ display_faceoff_details, setDisplayFaceoffDetails ] = useState(false);

	const display_total = props?.display_total ?? true;
	const display_complete_info = props?.display_complete_info ?? false;
	const display_origin = (display_complete_info) ? (props?.display_origin ?? false) : false;
	const display_prizes = (display_complete_info) ? (props?.display_prizes ?? false) : false;
	const stages = props?.stages ?? false;
	const promotion = props?.promotion ?? false;
	const tz = promotion.timezone;

	const has_game_stages = props?.has_game_stages ?? false;
	const has_quiz_stages = props?.has_quiz_stages ?? false;
	const has_voting_stages = props?.has_voting_stages ?? false;
	const has_team_stages = props?.has_team_stages ?? false;
	const has_form_stages = props?.has_form_stages ?? false;

	const participations = useRef(props?.stage_participations ?? { summary:[], total:0 });
	const display_date = props?.display_date ?? false;
	const has_ranking_enabled = (promotion && promotion.public_entries != '0') ? true : false;
	const has_tickets = (participations.current.summary.filter((p) => (p.StageTicket != undefined)).length > 0) ? true : false;

	const displayTicket = (participation) => {
		setDisplayTicketDetails(participation);
	};

	const displayDetails = (participation, is_media_content) => {
		const stage = stages.find((s) => (s.Stage.id == participation.StageScore.stage_id));

		if(participation.Stage.is_voting) {
			$.get(getRemotedbUrl(`/users/voted_summary/${promotion.id}/${stage.Stage.id}/${participation.StageScore.id}`), (res) => {
				setDisplayVotingStageId(stage.Stage.id);
				setDisplayVotingDetails(res);
			}, 'JSON');
			return;
		}

		if([spt.SCORE_PREDICTOR.id, spt.BRACKET_PREDICTOR.id].includes(parseInt(stage.Stage.promotion_type_id))) {
			setDisplayGuessScoreDetails({
				user:{
					...participation.StageScore,
					name:participation.StageScore.data,
				},
				created:participation.StageScore.created,
				promotion,
				stage:stage.Stage,
				teams:promotion.guess_score_teams,
				results:stage.Stage?.guess_score_matches_result ?? {},
				answers:participation.StageScore.quiz_entry.guess_score,
				user_id:participation.StageScore.id,
				locale:props.locale,
				intl:props.intl,
			});

			return;
		}

		if(participation.Stage.is_team) {
			setDisplayTeamDetails({
				...participation.StageScore.quiz_entry,
				created:participation.StageScore.created,
				score:participation.StageScore.score,
				promotion,
				stage:stage.Stage,
				user_id:participation.StageScore.id,
				locale:props.locale,
				intl:props.intl,
			});

			return;
		}

		const is_faceoff = ([spt.FACEOFF.id, spt.LIKEDISLIKE.id, spt.PICK_FAVORITE.id].includes(parseInt(stage.Stage.promotion_type_id))) ? true : false;

		if(is_faceoff) {
			setDisplayFaceoffDetails({
				user:{
					...participation.StageScore,
					name:participation.StageScore.data,
				},
				user_answer:participation.StageScore.quiz_entry,
				promotion,
				stage:{
					...stage.Stage,
					final_message:" ",
					faceoff_display_stats:false,
					//likedislike_display_stats:false,
				},
				stats:participation.stats,
				locale:props.locale,
				intl:props.intl,
				adaptToContainer:adaptGameToContainer
			});
			return;
		}

		setDisplayParticipationDetails(participation);
		
		if(is_media_content == undefined) is_media_content = false;
		if(is_media_content) return;

		if(props.on_view_detail != undefined) {
			const n_title = moment.tz(participation.created, 'Europe/Madrid').tz(tz);

			props.on_view_detail(n_title, <SquaredButton
				onClick={() => closeDetails()}
				icon='knk-arrow-l-big'
				_class={{ variable_icon_color:true }}
				aria-label="Close details"
			/>);
		}
	}

	const displayAdditionalInformation = (participation) => {
		setDisplayAdditionalParticipation(participation);
	};

	let media_detail;
	if(display_participation_details) {
		const part = display_participation_details;
		const qz = part?.StageScore?.quiz_entry ?? {};
		media_detail = qz?.media ?? false;
	}

	const total_participations = props?.total_participations ?? participations.current.summary.length;
	const has_more = (total_participations > max_paricipations_to_show) ? true : false;

	const part_with_completeness = participations.current.summary.find((p) => {
		if(isNotEmpty(p.Participation, 'completed')) return true;
		return false
	});
	const display_completeness_legend = (part_with_completeness != undefined) ? true : false;

	const display_actions = (display_complete_info || (has_game_stages || has_quiz_stages)) ? true : false;

	let num_cols = 2;
	if(display_date) num_cols++;
	if(display_origin) num_cols++;
	if(has_ranking_enabled) num_cols++;
	if(display_actions) num_cols++;

	if(total_participations == 0) {
		return (
			<div class="gui_participations_list">
				<p>{i18n("You haven't completed any entries yet.")}</p>
			</div>
		)
	}
	return (
		<div>
			<div class="gui_participations_list">
				{(display_completeness_legend) && (
					<RibbonCollection
						items={[{
							title:`${i18n("Completeness status legend")}:`,
							color:'transparent',
							autotranslate:false
						}, {
							title:"Stage completed",
							color:'green',
						}, {
							title:"Stage not completed",
							color:'red',
						}]}
						_class={{
							other:'not_in_bottom',
						}}
					/>
				)}

				{(participations.current.total != undefined && display_total) && (
					<div>{i18n("Total points")}: {formatNumber(participations.current.total)}</div>
				)}
				
				<table class={'single_user_participations' + ((display_total) ? ' gui_not_in_top_big' : '')} cellSpacing="0" cellPadding="0" style={{width:'100%'}}>
					<thead>
						<tr>
							<th class="hb_secondaryFont" style={(display_complete_info) ? { width:'11%' } : null} />

							<th class="mod_align_left gui_text_align_start hb_secondaryFont" style={(!display_complete_info) ? { width:'30%' } : null}>{i18n("Stage")}</th>

							{(display_date) && (
								<th class="mod_align_left gui_text_align_start hb_secondaryFont" style={(display_complete_info) ? { width:'180px' } : null}>{i18n("Date")}</th>
							)}

							{(display_origin) && (
								<th class="mod_align_left gui_text_align_start hb_secondaryFont" style={(display_complete_info) ? { width:'18%' } : null}>{i18n("Origin")}</th>
							)}

							{(has_ranking_enabled) && (
								<th class="mod_align_right gui_text_align_end hb_secondaryFont" style={(display_complete_info) ? { width:'10%' } : null}>{i18n("score", 'ucfirst')}</th>
							)}

							{(display_actions) && (
								<th class="hb_secondaryFont" style={(display_complete_info) ? { width:'12%' } : null} />
							)}
						</tr>
					</thead>

					<tbody>
						{participations.current.summary.map((part, i) => {
							let display_details = false;
							if(has_game_stages && part.Stage?.is_game) display_details = true;
							if(has_quiz_stages && part.Stage?.is_quiz) display_details = true;
							if(has_voting_stages && part.Stage?.is_voting) display_details = true;
							if(has_team_stages && part.Stage?.is_team) display_details = true;
							if(has_form_stages && part.Stage?.is_form) display_details = true;

							return (
								<SingleParticipation
									key={`part_${i}`}
									i={i}
									part={part}
									display_details={display_details}
									display_date={display_date}
									display_origin={display_origin}
									display_prizes={display_prizes}
									display_complete_info={display_complete_info}
									has_ranking_enabled={has_ranking_enabled}
									total_participations={total_participations}
									displayDetails={displayDetails}
									displayTicket={displayTicket}
									displayAdditionalInformation={displayAdditionalInformation}
									display_actions={display_actions}
									in_scores={props.in_scores}
									has_tickets={has_tickets}
									tz={tz}
									intl={props.intl}
								/>
							)
						})}

						{(has_more) && (
							<tr>
								<td colSpan={num_cols} class="blank_row">
									<FormattedMessage
										id="Only the last {num} participations are shown."
										values={{ num:max_paricipations_to_show }}
									/>
								</td>
							</tr>
						)}
					</tbody>
				</table>

				{(display_additional_participation !== false) && (
					<FloatingLayer _class={{size:'small'}} backButton={() => setDisplayAdditionalParticipation(false)}>
						<div class="without_actions">
							<SectionTitle>Additional information in participation</SectionTitle>

							{display_additional_participation.StageParticipation.map((cf, i) => {
								return (
									<div key={`cf_${i}`}>
										{(cf.title != "") && (
											<FieldLabel autotranslate={false}>{cf.title}</FieldLabel>
										)}

										{((cf?.config?.mediatype ?? {}) && cf.config.mediatype == 'image') && (
											<img src={cf.value} width="100%" alt="" />
										)}
									</div>
								)
							})}
						</div>
					</FloatingLayer>
				)}

				{(display_ticket_details !== false) && (
					<TicketLayer
						participation={display_ticket_details}
						promotion={promotion}
						backButton={() => setDisplayTicketDetails(false)}
						locale={props.locale}
						intl={props.intl}
					/>
				)}

				{(display_participation_details !== false && !media_detail) && (
					<FloatingLayerPublic
						pm={promotion}
						backButton={() => setDisplayParticipationDetails(false)}
					>
						<LeveledHTMLText
							_class={{ other:'gui_not_in_bottom_big' }}
						><p>{moment.tz(display_participation_details.StageScore.created, "Europe/Madrid").tz(tz).format('LL') + ", " + moment.tz(display_participation_details.StageScore.created, "Europe/Madrid").tz(tz).format('LT')}</p></LeveledHTMLText>

						{(isNotEmpty(display_participation_details.StageScore, 'time') || 
							isNotEmpty(display_participation_details.StageScore, 'score')) && (
							<LeveledHTMLText
								_class={{ other:'gui_participations_list'}}
								//style={{ minHeight:'100px' }}
							>
								<section class="gui_quiz_public_stats" style={{marginBottom:'2em'}}>
									<article class="gui_flexbox_auto gui_flex_align_center">

										{(isNotEmpty(display_participation_details.StageScore, 'time')) && (
											<p class="question_text">
												{i18n("Played time")}: <strong>&nbsp;{display_participation_details.StageScore.time} {i18n("sec")}</strong>
											</p>
										)}

										{(isNotEmpty(display_participation_details.StageScore, 'score')) && (
											<p class="question_text">
												{i18n("Score")}: <strong>&nbsp;{display_participation_details.StageScore.score} {i18n("points")}</strong>
											</p>
										)}
										
									</article>
								</section>
							</LeveledHTMLText>
						)}

						{(!display_participation_details.Stage.is_game) && (
							<QuizStatsPublic
								promotion_id={promotion.id}
								user_id={display_participation_details.StageScore.id}
								stage_id={display_participation_details.StageScore.stage}
							/>
						)}

						{(display_participation_details.StageScore.quiz_entry?.form != undefined) && (
							<div>
								<DisplayDynamicForm
									form={props.stages.find(st => (st.Stage.id == display_participation_details.StageScore.stage_id)).Stage.form}
									values={display_participation_details.StageScore.quiz_entry?.form}
								/>
							</div>
						)}
					</FloatingLayerPublic>
				)}
			</div>

			{(display_participation_details !== false && media_detail) && (
				<ul class="gui_multimedia_gallery">
					<SingleGallery
						user={{
							Participation:{
								...display_participation_details.StageScore,
								name:props.username,
							},
						}}
						selected_entry={true}
						promotion={promotion}
						stage={stages.find((m) => (m.Stage.id == display_participation_details.StageScore.stage_id)).Stage}
						in_registered={true}
						in_myparticipation={true}
						closeEntryAsLayer={() => setDisplayParticipationDetails(false)}
					/>
				</ul>
			)}

			{(display_voting_details !== false) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${i18n("Casted votes")}</h1>`}
					backButton={() => setDisplayVotingDetails(false)}
				>
					<div class="in_user_list">
						<MultiEntriesRanking
							promotion={promotion}
							participants={display_voting_details.data}
							autoload_data={false}
							stages={stages}
							stage_id={display_voting_stage_id}
							in_layer={true}
							is_logged={false}
							is_builder={true}
							avail_participations={false}
						/>
					</div>
				</FloatingLayerPublic>
			)}

			{(display_team_details !== false) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${props.intl.formatMessage({ id: "The team of {name}" }, { name:props.username })}</h1>`}
					backButton={() => setDisplayTeamDetails(false)}
				>
					<div class="in_user_list">
						<UserTeamParticipationDetail
							{...display_team_details}
						/>
					</div>
				</FloatingLayerPublic>
			)}

			{(display_guess_score_details !== false) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${props.intl.formatMessage({ id: "The prediction of {name}" }, { name:props.username })}</h1>`}
					backButton={() => setDisplayGuessScoreDetails(false)}
				>
					{(display_guess_score_details.stage.promotion_type_id != spt.SCORE_PREDICTOR.id && isNotEmpty(display_guess_score_details.user, 'score')) && (
						<div class="gui_flexbox_auto">
							{i18n("Score")}: <strong class="gui_not_in_start_micro">{display_guess_score_details.user.score} {i18n("points")}</strong>
						</div>
					)}
					
					<div class="in_user_list gui_game_wrapper">
						{(display_guess_score_details.stage.promotion_type_id == spt.BRACKET_PREDICTOR.id) && (
							<OneTimeBracketFinal
								{...display_guess_score_details}
								is_standalone={true}
								only_contents={true}
							/>
						)}

						{(display_guess_score_details.stage.promotion_type_id == spt.SCORE_PREDICTOR.id) && (
							<GuessScoreResumeWithPoints
								{...display_guess_score_details}
							/>
						)}
					</div>
				</FloatingLayerPublic>
			)}

			{(display_faceoff_details) && (
				<FloatingLayerPublic
					pm={promotion}
					title={`<h1>${props.intl.formatMessage({ id: "The choice of {name}" }, { name:props.username })}</h1>`}
					backButton={() => setDisplayFaceoffDetails(false)}
				>
					<div class="in_user_list">
						{(display_faceoff_details.stage.promotion_type_id == spt.FACEOFF.id) && (
							<BattleFinal
								{...display_faceoff_details}
							/>
						)}

						{(display_faceoff_details.stage.promotion_type_id == spt.PICK_FAVORITE.id) && (
							<PickFavoriteFinal
								in_ranking={true}
								{...display_faceoff_details}
							/>
						)}

						{(display_faceoff_details.stage.promotion_type_id == spt.LIKEDISLIKE.id) && (
							<LikeDislikeFinal
								{...display_faceoff_details}
							/>
						)}
					</div>

					{(isNotEmpty(display_faceoff_details.user, 'score')) && (
						<div class="gui_flexbox_auto gui_flex_center">
							{i18n("Score")}: <strong class="gui_not_in_start_micro">{display_faceoff_details.user.score} {i18n("points")}</strong>
						</div>
					)}
				</FloatingLayerPublic>
			)}
		</div>
	);
}

export default injectIntl(UserParticipationsMultistageList);